/**
 * Forum page translations (all 8 locales).
 *
 * Only UI strings live here - categories / threads come live from the
 * `forumCategories` and `forumThreads` Firestore collections.
 */

import type { LanguageMap } from '@models/i18n';

export interface ForumDict {
    back: string;
    title: string;
    subtitle: string;

    // Search + actions
    searchPlaceholder: string;
    newThread: string;
    loginToPost: string;
    readOnlyBadge: string;
    readOnlyDesc: string;
    postingBadge: string;
    postingDesc: string;
    hiddenBadge: string;
    hiddenDesc: string;
    threadCreateTitle: string;
    threadCreateDesc: string;
    threadCreateCategory: string;
    threadCreateSubject: string;
    threadCreateSubjectPlaceholder: string;
    threadCreateMessage: string;
    threadCreateMessagePlaceholder: string;
    threadCreateSubmit: string;
    threadBackToList: string;
    threadReplyPlaceholder: string;
    threadReplySubmit: string;
    threadReplyLogin: string;
    threadLoading: string;
    threadNotFound: string;
    threadCreateSuccess: string;
    threadCreateError: string;
    threadReplyError: string;

    // Category list headings
    categoriesTitle: string;
    threadsCount: string; // e.g. "{count} тредов"
    postsCount: string;   // e.g. "{count} сообщений"
    lastPost: string;
    noPosts: string;

    // Category names + descriptions
    catDiscussions: string;
    catDiscussionsDesc: string;
    catGuides: string;
    catGuidesDesc: string;
    catBugs: string;
    catBugsDesc: string;
    catSuggestions: string;
    catSuggestionsDesc: string;
    catOfftopic: string;
    catOfftopicDesc: string;

    // Recent threads block
    recentTitle: string;
    recentSubtitle: string;
    replies: string;     // "{count} ответов"
    views: string;       // "{count} просмотров"
    agoSuffix: string;   // e.g. "назад" - combined with relative time

    // Empty state
    emptyTitle: string;
    emptyDesc: string;
    emptyThreads: string;

    // Live-data UX states
    loadError: string;
}

export const forumTranslations: LanguageMap<ForumDict> = {
    RU: {
        back: '← На главную',
        title: 'Форум',
        subtitle: 'Обсуждения, гайды и общение с сообществом.',
        searchPlaceholder: 'Поиск по форуму...',
        newThread: 'Новая тема',
        loginToPost: 'Войдите, чтобы создать тему',
        readOnlyBadge: 'Только чтение',
        readOnlyDesc: 'Публикация тем скоро появится. Пока можно читать и искать обсуждения.',
        postingBadge: 'Публикация открыта',
        postingDesc: 'Теперь можно создавать темы и отвечать в обсуждениях.',
        hiddenBadge: 'Скрыто',
        hiddenDesc: 'Форум временно скрыт до следующего этапа разработки.',
        threadCreateTitle: 'Новая тема',
        threadCreateDesc: 'Создайте обсуждение прямо на сайте и откройте его для сообщества.',
        threadCreateCategory: 'Категория',
        threadCreateSubject: 'Заголовок',
        threadCreateSubjectPlaceholder: 'Напишите заголовок темы',
        threadCreateMessage: 'Сообщение',
        threadCreateMessagePlaceholder: 'Опишите тему, вопрос или новость',
        threadCreateSubmit: 'Опубликовать тему',
        threadBackToList: '← К обсуждениям',
        threadReplyPlaceholder: 'Напишите ответ...',
        threadReplySubmit: 'Ответить',
        threadReplyLogin: 'Войдите, чтобы ответить в теме',
        threadLoading: 'Загрузка обсуждения...',
        threadNotFound: 'Тема не найдена или уже удалена.',
        threadCreateSuccess: 'Тема опубликована',
        threadCreateError: 'Не удалось опубликовать тему.',
        threadReplyError: 'Не удалось отправить ответ.',
        categoriesTitle: 'Категории',
        threadsCount: 'тем',
        postsCount: 'сообщений',
        lastPost: 'Последнее сообщение',
        noPosts: 'Пока нет сообщений',
        catDiscussions: 'Общие обсуждения',
        catDiscussionsDesc: 'Разговоры об игре, анонсы, впечатления',
        catGuides: 'Гайды и советы',
        catGuidesDesc: 'Инструкции, прохождения, билды',
        catBugs: 'Баги и проблемы',
        catBugsDesc: 'Сообщения о багах и технических проблемах',
        catSuggestions: 'Предложения',
        catSuggestionsDesc: 'Идеи по улучшению игры',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Общение не по теме игры',
        recentTitle: 'Последние обсуждения',
        recentSubtitle: 'Самые свежие темы со всех категорий.',
        replies: 'ответов',
        views: 'просмотров',
        agoSuffix: 'назад',
        emptyTitle: 'Ничего не найдено',
        emptyDesc: 'Попробуйте другой запрос или выберите категорию.',
        emptyThreads: 'Публичные обсуждения скоро появятся. Пока форум доступен только для чтения.',
        loadError: 'Не удалось загрузить форум. Попробуйте обновить страницу.',
    },
    EN: {
        back: '← Back to home',
        title: 'Forum',
        subtitle: 'Discussions, guides and community chat.',
        searchPlaceholder: 'Search the forum...',
        newThread: 'New thread',
        loginToPost: 'Sign in to create a thread',
        readOnlyBadge: 'Read-only',
        readOnlyDesc: 'Thread publishing is coming soon. For now you can read and search discussions.',
        postingBadge: 'Posting open',
        postingDesc: 'You can now create threads and reply in discussions.',
        hiddenBadge: 'Hidden',
        hiddenDesc: 'The forum is temporarily hidden until the next development stage.',
        threadCreateTitle: 'New thread',
        threadCreateDesc: 'Create a discussion directly on the site and open it to the community.',
        threadCreateCategory: 'Category',
        threadCreateSubject: 'Title',
        threadCreateSubjectPlaceholder: 'Write the thread title',
        threadCreateMessage: 'Message',
        threadCreateMessagePlaceholder: 'Describe the topic, question or news',
        threadCreateSubmit: 'Publish thread',
        threadBackToList: '← Back to discussions',
        threadReplyPlaceholder: 'Write a reply...',
        threadReplySubmit: 'Reply',
        threadReplyLogin: 'Sign in to reply in this thread',
        threadLoading: 'Loading discussion...',
        threadNotFound: 'Thread not found or already removed.',
        threadCreateSuccess: 'Thread published',
        threadCreateError: 'Failed to publish the thread.',
        threadReplyError: 'Failed to send the reply.',
        categoriesTitle: 'Categories',
        threadsCount: 'threads',
        postsCount: 'posts',
        lastPost: 'Last post',
        noPosts: 'No posts yet',
        catDiscussions: 'General discussions',
        catDiscussionsDesc: 'Talk about the game, announcements, thoughts',
        catGuides: 'Guides & tips',
        catGuidesDesc: 'Walkthroughs, strategies, builds',
        catBugs: 'Bugs & issues',
        catBugsDesc: 'Bug reports and technical problems',
        catSuggestions: 'Suggestions',
        catSuggestionsDesc: 'Ideas to improve the game',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Chat about anything else',
        recentTitle: 'Recent discussions',
        recentSubtitle: 'Latest threads across all categories.',
        replies: 'replies',
        views: 'views',
        agoSuffix: 'ago',
        emptyTitle: 'Nothing found',
        emptyDesc: 'Try another search or pick a category.',
        emptyThreads: 'Public discussions are coming soon. The forum is currently read-only.',
        loadError: 'Failed to load the forum. Please refresh the page.',
    },
    DE: {
        back: '← Zur Startseite',
        title: 'Forum',
        subtitle: 'Diskussionen, Guides und Community-Chat.',
        searchPlaceholder: 'Forum durchsuchen...',
        newThread: 'Neues Thema',
        loginToPost: 'Anmelden, um ein Thema zu erstellen',
        readOnlyBadge: 'Nur Lesen',
        readOnlyDesc: 'Das Erstellen neuer Themen folgt bald. Derzeit können Sie Diskussionen lesen und durchsuchen.',
        postingBadge: 'Posting aktiv',
        postingDesc: 'Sie können jetzt Themen erstellen und in Diskussionen antworten.',
        hiddenBadge: 'Versteckt',
        hiddenDesc: 'Das Forum ist bis zur nächsten Entwicklungsphase vorübergehend ausgeblendet.',
        threadCreateTitle: 'Neues Thema',
        threadCreateDesc: 'Erstellen Sie direkt auf der Website eine Diskussion für die Community.',
        threadCreateCategory: 'Kategorie',
        threadCreateSubject: 'Titel',
        threadCreateSubjectPlaceholder: 'Schreiben Sie den Thementitel',
        threadCreateMessage: 'Nachricht',
        threadCreateMessagePlaceholder: 'Beschreiben Sie Thema, Frage oder Neuigkeit',
        threadCreateSubmit: 'Thema veröffentlichen',
        threadBackToList: '← Zurück zu Diskussionen',
        threadReplyPlaceholder: 'Antwort schreiben...',
        threadReplySubmit: 'Antworten',
        threadReplyLogin: 'Melden Sie sich an, um in diesem Thema zu antworten',
        threadLoading: 'Diskussion wird geladen...',
        threadNotFound: 'Thema nicht gefunden oder bereits entfernt.',
        threadCreateSuccess: 'Thema veröffentlicht',
        threadCreateError: 'Thema konnte nicht veröffentlicht werden.',
        threadReplyError: 'Antwort konnte nicht gesendet werden.',
        categoriesTitle: 'Kategorien',
        threadsCount: 'Themen',
        postsCount: 'Beiträge',
        lastPost: 'Letzter Beitrag',
        noPosts: 'Noch keine Beiträge',
        catDiscussions: 'Allgemeine Diskussionen',
        catDiscussionsDesc: 'Gespräche über das Spiel, Ankündigungen, Eindrücke',
        catGuides: 'Guides & Tipps',
        catGuidesDesc: 'Anleitungen, Walkthroughs, Builds',
        catBugs: 'Bugs & Probleme',
        catBugsDesc: 'Fehlerberichte und technische Probleme',
        catSuggestions: 'Vorschläge',
        catSuggestionsDesc: 'Ideen zur Verbesserung',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Gespräche über alles andere',
        recentTitle: 'Neueste Diskussionen',
        recentSubtitle: 'Die neuesten Themen aus allen Kategorien.',
        replies: 'Antworten',
        views: 'Aufrufe',
        agoSuffix: 'her',
        emptyTitle: 'Nichts gefunden',
        emptyDesc: 'Andere Suche versuchen oder Kategorie wählen.',
        emptyThreads: 'Öffentliche Diskussionen folgen bald. Das Forum ist derzeit nur lesbar.',
        loadError: 'Forum konnte nicht geladen werden. Bitte aktualisiere die Seite.',
    },
    FR: {
        back: '← Retour à l\'accueil',
        title: 'Forum',
        subtitle: 'Discussions, guides et chat communautaire.',
        searchPlaceholder: 'Rechercher dans le forum...',
        newThread: 'Nouveau sujet',
        loginToPost: 'Connectez-vous pour créer un sujet',
        readOnlyBadge: 'Lecture seule',
        readOnlyDesc: 'La publication de sujets arrive bientôt. Pour le moment, vous pouvez lire et rechercher des discussions.',
        postingBadge: 'Publication ouverte',
        postingDesc: 'Vous pouvez maintenant créer des sujets et répondre dans les discussions.',
        hiddenBadge: 'Masqué',
        hiddenDesc: 'Le forum est temporairement masqué jusqu\'à la prochaine phase de développement.',
        threadCreateTitle: 'Nouveau sujet',
        threadCreateDesc: 'Créez une discussion directement sur le site pour la communauté.',
        threadCreateCategory: 'Catégorie',
        threadCreateSubject: 'Titre',
        threadCreateSubjectPlaceholder: 'Saisissez le titre du sujet',
        threadCreateMessage: 'Message',
        threadCreateMessagePlaceholder: 'Décrivez le sujet, la question ou la nouveauté',
        threadCreateSubmit: 'Publier le sujet',
        threadBackToList: '← Retour aux discussions',
        threadReplyPlaceholder: 'Écrivez une réponse...',
        threadReplySubmit: 'Répondre',
        threadReplyLogin: 'Connectez-vous pour répondre dans ce sujet',
        threadLoading: 'Chargement de la discussion...',
        threadNotFound: 'Sujet introuvable ou déjà supprimé.',
        threadCreateSuccess: 'Sujet publié',
        threadCreateError: 'Impossible de publier le sujet.',
        threadReplyError: 'Impossible d\'envoyer la réponse.',
        categoriesTitle: 'Catégories',
        threadsCount: 'sujets',
        postsCount: 'messages',
        lastPost: 'Dernier message',
        noPosts: 'Pas encore de messages',
        catDiscussions: 'Discussions générales',
        catDiscussionsDesc: 'Discussions sur le jeu, annonces, impressions',
        catGuides: 'Guides & astuces',
        catGuidesDesc: 'Tutos, soluces, builds',
        catBugs: 'Bugs & problèmes',
        catBugsDesc: 'Rapports de bugs et problèmes techniques',
        catSuggestions: 'Suggestions',
        catSuggestionsDesc: 'Idées pour améliorer le jeu',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Discussions hors-sujet',
        recentTitle: 'Discussions récentes',
        recentSubtitle: 'Les derniers sujets de toutes les catégories.',
        replies: 'réponses',
        views: 'vues',
        agoSuffix: '',
        emptyTitle: 'Rien trouvé',
        emptyDesc: 'Essayez une autre recherche ou choisissez une catégorie.',
        emptyThreads: 'Les discussions publiques arrivent bientôt. Le forum est actuellement en lecture seule.',
        loadError: 'Impossible de charger le forum. Actualisez la page.',
    },
    PL: {
        back: '← Powrót do strony głównej',
        title: 'Forum',
        subtitle: 'Dyskusje, poradniki i czat społeczności.',
        searchPlaceholder: 'Szukaj na forum...',
        newThread: 'Nowy wątek',
        loginToPost: 'Zaloguj się, aby utworzyć wątek',
        readOnlyBadge: 'Tylko do odczytu',
        readOnlyDesc: 'Publikowanie wątków pojawi się wkrótce. Na razie możesz czytać i wyszukiwać dyskusje.',
        postingBadge: 'Publikowanie otwarte',
        postingDesc: 'Możesz teraz tworzyć wątki i odpowiadać w dyskusjach.',
        hiddenBadge: 'Ukryte',
        hiddenDesc: 'Forum jest tymczasowo ukryte do następnego etapu rozwoju.',
        threadCreateTitle: 'Nowy wątek',
        threadCreateDesc: 'Utwórz dyskusję bezpośrednio na stronie dla społeczności.',
        threadCreateCategory: 'Kategoria',
        threadCreateSubject: 'Tytuł',
        threadCreateSubjectPlaceholder: 'Wpisz tytuł wątku',
        threadCreateMessage: 'Wiadomość',
        threadCreateMessagePlaceholder: 'Opisz temat, pytanie lub nowość',
        threadCreateSubmit: 'Opublikuj wątek',
        threadBackToList: '← Wróć do dyskusji',
        threadReplyPlaceholder: 'Napisz odpowiedź...',
        threadReplySubmit: 'Odpowiedz',
        threadReplyLogin: 'Zaloguj się, aby odpowiedzieć w tym wątku',
        threadLoading: 'Ładowanie dyskusji...',
        threadNotFound: 'Wątek nie został znaleziony lub został usunięty.',
        threadCreateSuccess: 'Wątek opublikowany',
        threadCreateError: 'Nie udało się opublikować wątku.',
        threadReplyError: 'Nie udało się wysłać odpowiedzi.',
        categoriesTitle: 'Kategorie',
        threadsCount: 'wątków',
        postsCount: 'postów',
        lastPost: 'Ostatni post',
        noPosts: 'Brak postów',
        catDiscussions: 'Ogólne dyskusje',
        catDiscussionsDesc: 'Rozmowy o grze, ogłoszenia, wrażenia',
        catGuides: 'Poradniki i wskazówki',
        catGuidesDesc: 'Instrukcje, przejścia, buildy',
        catBugs: 'Błędy i problemy',
        catBugsDesc: 'Raporty błędów i problemy techniczne',
        catSuggestions: 'Sugestie',
        catSuggestionsDesc: 'Pomysły na ulepszenie gry',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Rozmowy nie na temat gry',
        recentTitle: 'Ostatnie dyskusje',
        recentSubtitle: 'Najnowsze wątki ze wszystkich kategorii.',
        replies: 'odpowiedzi',
        views: 'wyświetleń',
        agoSuffix: 'temu',
        emptyTitle: 'Nic nie znaleziono',
        emptyDesc: 'Spróbuj innego zapytania lub wybierz kategorię.',
        emptyThreads: 'Publiczne dyskusje pojawią się wkrótce. Forum jest obecnie tylko do odczytu.',
        loadError: 'Nie udało się załadować forum. Odśwież stronę.',
    },
    ES: {
        back: '← Volver al inicio',
        title: 'Foro',
        subtitle: 'Discusiones, guías y chat de comunidad.',
        searchPlaceholder: 'Buscar en el foro...',
        newThread: 'Nuevo tema',
        loginToPost: 'Inicia sesión para crear un tema',
        readOnlyBadge: 'Solo lectura',
        readOnlyDesc: 'La publicación de temas llegará pronto. Por ahora puedes leer y buscar discusiones.',
        postingBadge: 'Publicación abierta',
        postingDesc: 'Ahora puedes crear temas y responder en las discusiones.',
        hiddenBadge: 'Oculto',
        hiddenDesc: 'El foro está oculto temporalmente hasta la siguiente etapa de desarrollo.',
        threadCreateTitle: 'Nuevo tema',
        threadCreateDesc: 'Crea una discusión directamente en el sitio para la comunidad.',
        threadCreateCategory: 'Categoría',
        threadCreateSubject: 'Título',
        threadCreateSubjectPlaceholder: 'Escribe el título del tema',
        threadCreateMessage: 'Mensaje',
        threadCreateMessagePlaceholder: 'Describe el tema, la pregunta o la novedad',
        threadCreateSubmit: 'Publicar tema',
        threadBackToList: '← Volver a discusiones',
        threadReplyPlaceholder: 'Escribe una respuesta...',
        threadReplySubmit: 'Responder',
        threadReplyLogin: 'Inicia sesión para responder en este tema',
        threadLoading: 'Cargando discusión...',
        threadNotFound: 'Tema no encontrado o ya eliminado.',
        threadCreateSuccess: 'Tema publicado',
        threadCreateError: 'No se pudo publicar el tema.',
        threadReplyError: 'No se pudo enviar la respuesta.',
        categoriesTitle: 'Categorías',
        threadsCount: 'temas',
        postsCount: 'mensajes',
        lastPost: 'Último mensaje',
        noPosts: 'Aún no hay mensajes',
        catDiscussions: 'Discusiones generales',
        catDiscussionsDesc: 'Conversaciones sobre el juego, anuncios, impresiones',
        catGuides: 'Guías y consejos',
        catGuidesDesc: 'Tutoriales, walkthroughs, builds',
        catBugs: 'Bugs y problemas',
        catBugsDesc: 'Reportes de bugs y problemas técnicos',
        catSuggestions: 'Sugerencias',
        catSuggestionsDesc: 'Ideas para mejorar el juego',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Charlas de otros temas',
        recentTitle: 'Discusiones recientes',
        recentSubtitle: 'Temas más recientes de todas las categorías.',
        replies: 'respuestas',
        views: 'visitas',
        agoSuffix: '',
        emptyTitle: 'Nada encontrado',
        emptyDesc: 'Prueba otra búsqueda o elige una categoría.',
        emptyThreads: 'Las discusiones públicas llegarán pronto. El foro está en modo solo lectura.',
        loadError: 'No se pudo cargar el foro. Actualice la página.',
    },
    CZ: {
        back: '← Zpět na hlavní',
        title: 'Fórum',
        subtitle: 'Diskuze, návody a komunitní chat.',
        searchPlaceholder: 'Hledat na fóru...',
        newThread: 'Nové téma',
        loginToPost: 'Přihlaste se pro vytvoření tématu',
        readOnlyBadge: 'Jen pro čtení',
        readOnlyDesc: 'Publikování témat bude brzy k dispozici. Zatím lze diskuze číst a vyhledávat.',
        postingBadge: 'Publikování otevřeno',
        postingDesc: 'Nyní můžete vytvářet témata a odpovídat v diskuzích.',
        hiddenBadge: 'Skryto',
        hiddenDesc: 'Fórum je dočasně skryté do další fáze vývoje.',
        threadCreateTitle: 'Nové téma',
        threadCreateDesc: 'Vytvořte diskuzi přímo na webu pro komunitu.',
        threadCreateCategory: 'Kategorie',
        threadCreateSubject: 'Název',
        threadCreateSubjectPlaceholder: 'Napište název tématu',
        threadCreateMessage: 'Zpráva',
        threadCreateMessagePlaceholder: 'Popište téma, otázku nebo novinku',
        threadCreateSubmit: 'Publikovat téma',
        threadBackToList: '← Zpět k diskuzím',
        threadReplyPlaceholder: 'Napište odpověď...',
        threadReplySubmit: 'Odpovědět',
        threadReplyLogin: 'Přihlaste se pro odpověď v tomto tématu',
        threadLoading: 'Načítání diskuze...',
        threadNotFound: 'Téma nebylo nalezeno nebo již bylo odstraněno.',
        threadCreateSuccess: 'Téma publikováno',
        threadCreateError: 'Nepodařilo se publikovat téma.',
        threadReplyError: 'Nepodařilo se odeslat odpověď.',
        categoriesTitle: 'Kategorie',
        threadsCount: 'témat',
        postsCount: 'příspěvků',
        lastPost: 'Poslední příspěvek',
        noPosts: 'Zatím žádné příspěvky',
        catDiscussions: 'Obecné diskuze',
        catDiscussionsDesc: 'Rozhovory o hře, oznámení, dojmy',
        catGuides: 'Návody a tipy',
        catGuidesDesc: 'Návody, průchody, buildy',
        catBugs: 'Bugy a problémy',
        catBugsDesc: 'Hlášení bugů a technické problémy',
        catSuggestions: 'Návrhy',
        catSuggestionsDesc: 'Nápady na vylepšení hry',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Rozhovory mimo téma hry',
        recentTitle: 'Nedávné diskuze',
        recentSubtitle: 'Nejnovější témata ze všech kategorií.',
        replies: 'odpovědí',
        views: 'zobrazení',
        agoSuffix: 'zpět',
        emptyTitle: 'Nic nenalezeno',
        emptyDesc: 'Zkuste jiné hledání nebo vyberte kategorii.',
        emptyThreads: 'Veřejné diskuze budou brzy dostupné. Fórum je zatím jen pro čtení.',
        loadError: 'Nepodařilo se načíst fórum. Obnovte stránku.',
    },
    IT: {
        back: '← Torna alla home',
        title: 'Forum',
        subtitle: 'Discussioni, guide e chat della community.',
        searchPlaceholder: 'Cerca nel forum...',
        newThread: 'Nuova discussione',
        loginToPost: 'Accedi per creare una discussione',
        readOnlyBadge: 'Solo lettura',
        readOnlyDesc: 'La pubblicazione di discussioni arriverà presto. Per ora puoi leggere e cercare nelle discussioni.',
        postingBadge: 'Pubblicazione aperta',
        postingDesc: 'Ora puoi creare discussioni e rispondere nei thread.',
        hiddenBadge: 'Nascosto',
        hiddenDesc: 'Il forum è temporaneamente nascosto fino alla prossima fase di sviluppo.',
        threadCreateTitle: 'Nuova discussione',
        threadCreateDesc: 'Crea una discussione direttamente sul sito per la community.',
        threadCreateCategory: 'Categoria',
        threadCreateSubject: 'Titolo',
        threadCreateSubjectPlaceholder: 'Scrivi il titolo della discussione',
        threadCreateMessage: 'Messaggio',
        threadCreateMessagePlaceholder: 'Descrivi il tema, la domanda o la novità',
        threadCreateSubmit: 'Pubblica discussione',
        threadBackToList: '← Torna alle discussioni',
        threadReplyPlaceholder: 'Scrivi una risposta...',
        threadReplySubmit: 'Rispondi',
        threadReplyLogin: 'Accedi per rispondere in questa discussione',
        threadLoading: 'Caricamento discussione...',
        threadNotFound: 'Discussione non trovata o già rimossa.',
        threadCreateSuccess: 'Discussione pubblicata',
        threadCreateError: 'Impossibile pubblicare la discussione.',
        threadReplyError: 'Impossibile inviare la risposta.',
        categoriesTitle: 'Categorie',
        threadsCount: 'discussioni',
        postsCount: 'messaggi',
        lastPost: 'Ultimo messaggio',
        noPosts: 'Nessun messaggio ancora',
        catDiscussions: 'Discussioni generali',
        catDiscussionsDesc: 'Parla del gioco, annunci, impressioni',
        catGuides: 'Guide e consigli',
        catGuidesDesc: 'Tutorial, walkthrough, build',
        catBugs: 'Bug e problemi',
        catBugsDesc: 'Segnalazioni di bug e problemi tecnici',
        catSuggestions: 'Suggerimenti',
        catSuggestionsDesc: 'Idee per migliorare il gioco',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Chat su qualsiasi altra cosa',
        recentTitle: 'Discussioni recenti',
        recentSubtitle: 'Le ultime discussioni da tutte le categorie.',
        replies: 'risposte',
        views: 'visualizzazioni',
        agoSuffix: 'fa',
        emptyTitle: 'Nessun risultato',
        emptyDesc: 'Prova un\'altra ricerca o scegli una categoria.',
        emptyThreads: 'Le discussioni pubbliche arriveranno presto. Il forum è attualmente in sola lettura.',
        loadError: 'Impossibile caricare il forum. Aggiorna la pagina.',
    },

    UA: {
        back: '← На головну',
        title: 'Форум',
        subtitle: 'Обговорення, гайди та спілкування зі спільнотою.',
        searchPlaceholder: 'Пошук по форуму...',
        newThread: 'Нова тема',
        loginToPost: 'Увійдіть, щоб створити тему',
        readOnlyBadge: 'Тільки читання',
        readOnlyDesc: 'Публікація тем скоро з\'явиться. Поки можна читати та шукати обговорення.',
        postingBadge: 'Публікація відкрита',
        postingDesc: 'Тепер можна створювати теми та відповідати в обговореннях.',
        hiddenBadge: 'Приховано',
        hiddenDesc: 'Форум тимчасово приховано до наступного етапу розробки.',
        threadCreateTitle: 'Нова тема',
        threadCreateDesc: 'Створіть обговорення прямо на сайті та відкрийте його для спільноти.',
        threadCreateCategory: 'Категорія',
        threadCreateSubject: 'Заголовок',
        threadCreateSubjectPlaceholder: 'Напишіть заголовок теми',
        threadCreateMessage: 'Повідомлення',
        threadCreateMessagePlaceholder: 'Опишіть тему, питання або новину',
        threadCreateSubmit: 'Опублікувати тему',
        threadBackToList: '← До обговорень',
        threadReplyPlaceholder: 'Напишіть відповідь...',
        threadReplySubmit: 'Відповісти',
        threadReplyLogin: 'Увійдіть, щоб відповісти в темі',
        threadLoading: 'Завантаження обговорення...',
        threadNotFound: 'Тему не знайдено або вже видалено.',
        threadCreateSuccess: 'Тему опубліковано',
        threadCreateError: 'Не вдалося опублікувати тему.',
        threadReplyError: 'Не вдалося надіслати відповідь.',
        categoriesTitle: 'Категорії',
        threadsCount: 'тем',
        postsCount: 'повідомлень',
        lastPost: 'Останнє повідомлення',
        noPosts: 'Поки немає повідомлень',
        catDiscussions: 'Загальні обговорення',
        catDiscussionsDesc: 'Розмови про гру, анонси, враження',
        catGuides: 'Гайди та поради',
        catGuidesDesc: 'Інструкції, проходження, білди',
        catBugs: 'Баги та проблеми',
        catBugsDesc: 'Повідомлення про баги та технічні проблеми',
        catSuggestions: 'Пропозиції',
        catSuggestionsDesc: 'Ідеї щодо покращення гри',
        catOfftopic: 'Off-topic',
        catOfftopicDesc: 'Спілкування не по темі гри',
        recentTitle: 'Останні обговорення',
        recentSubtitle: 'Найсвіжіші теми з усіх категорій.',
        replies: 'відповідей',
        views: 'переглядів',
        agoSuffix: 'тому',
        emptyTitle: 'Нічого не знайдено',
        emptyDesc: 'Спробуйте інший запит або виберіть категорію.',
        emptyThreads: 'Публічні обговорення скоро з\'являться. Поки форум доступний тільки для читання.',
        loadError: 'Не вдалося завантажити форум. Спробуйте оновити сторінку.',
    },
};
